// SVG port of the Discharge / Water Temperature ChartCards in
// GaugeDetailView.swift: one line with a soft area wash, daily gridlines, the
// latest value labelled at the line's end, and a crosshair tooltip.
import { useMemo } from 'react';
import type { JSX, PointerEvent } from 'react';
import { formatDateTime, formatNumber } from '../../lib/format';
import type { USGSSeriesPoint } from '../../types/usgs';
import { closeArea, extent, formatAxisValue, linearScale, monotonePath, nearestIndex, niceTicks, paddedDomain, timeTicks } from './chartMath';
import { DataTable, MARGIN, Tooltip, XAxis, YGrid, pointerX, useActiveIndex, useContainerWidth } from './chartParts';
import { describeSeriesChart } from './model';

export function SeriesChart({
  name,
  points,
  unit,
  digits,
  color,
  height,
}: {
  name: string;
  /** Time-sorted readings. */
  points: USGSSeriesPoint[];
  unit: string;
  /** Fraction digits for values in the tooltip, end label and table. */
  digits: number;
  /** CSS color (normally a `--chart-*` token). */
  color: string;
  height: number;
}): JSX.Element | null {
  const { ref, width } = useContainerWidth();
  const { index, setActive, onKeyDown } = useActiveIndex(points.length, points.length - 1);

  const model = useMemo(() => {
    const times = points.map((p) => p.date.getTime());
    const t = extent(times);
    const v = extent(points.map((p) => p.value));
    if (!t || !v) return null;
    let [tMin, tMax] = t;
    if (tMin === tMax) {
      tMin -= 3_600_000;
      tMax += 3_600_000;
    }
    const [vLo, vHi] = paddedDomain(v[0], v[1], 0.08);
    let yTicks = niceTicks(vLo, vHi, 4);
    if (yTicks.length < 2) yTicks = [vLo, vHi];
    const plotRight = width - MARGIN.right;
    const plotBottom = height - MARGIN.bottom;
    const x = linearScale(tMin, tMax, MARGIN.left, plotRight);
    const y = linearScale(yTicks[0], yTicks[yTicks.length - 1], plotBottom, MARGIN.top);
    const plotWidth = plotRight - MARGIN.left;
    const xt = timeTicks(tMin, tMax, Math.max(2, Math.min(7, Math.floor(plotWidth / 56))));
    const pts = points.map((p) => ({ x: x(p.date.getTime()), y: y(p.value) }));
    const linePath = monotonePath(pts);
    return {
      times,
      x,
      y,
      yTicks,
      xTicks: xt.ticks,
      stepMs: xt.stepMs,
      plotRight,
      plotBottom,
      linePath,
      areaPath: closeArea(linePath, pts, plotBottom),
      last: pts[pts.length - 1],
      description: describeSeriesChart(name, points, unit, digits),
    };
  }, [points, width, height, name, unit, digits]);

  if (!model) return null;
  const { x, y, times, plotRight, plotBottom, last } = model;
  const lastPoint = points[points.length - 1];
  const active = index != null ? points[index] : null;
  const activeX = active ? x(active.date.getTime()) : 0;

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    setActive(nearestIndex(times, x.invert(pointerX(e, width))));
  };

  // The end label hugs the last point; it moves below when the line ends near the top.
  const endLabelY = last.y - 9 < MARGIN.top - 6 ? last.y + 16 : last.y - 9;

  return (
    <div className="gd-chart" ref={ref}>
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={model.description}
        tabIndex={0}
        onPointerMove={onPointerMove}
        onPointerLeave={() => setActive(null)}
        onKeyDown={onKeyDown}
        onBlur={() => setActive(null)}
      >
        <YGrid ticks={model.yTicks} y={y} x0={MARGIN.left} x1={plotRight} format={formatAxisValue} />
        <XAxis ticks={model.xTicks} stepMs={model.stepMs} x={x} x0={MARGIN.left} x1={plotRight} yTop={MARGIN.top} yBottom={plotBottom} height={height} />

        <g style={{ color }}>
          {model.areaPath ? <path className="gd-area" d={model.areaPath} fill="currentColor" /> : null}
          {model.linePath ? <path className="gd-line" d={model.linePath} stroke="currentColor" /> : null}
          <circle className="gd-end-dot" cx={last.x} cy={last.y} r={4} fill="currentColor" />
        </g>
        <text className="gd-end-label" x={last.x - 2} y={endLabelY} textAnchor="end">
          {formatNumber(lastPoint.value, digits)} {unit}
        </text>

        {active ? (
          <g>
            <line className="gd-crosshair" x1={activeX} x2={activeX} y1={MARGIN.top} y2={plotBottom} />
            <circle className="gd-active-dot" cx={activeX} cy={y(active.value)} r={4} style={{ fill: color }} />
          </g>
        ) : null}
      </svg>

      {active ? <Tooltip x={activeX} width={width} time={active.date} rows={[{ value: `${formatNumber(active.value, digits)} ${unit}`, label: name, color }]} /> : null}

      <DataTable
        summary={`Data table (${points.length} readings)`}
        columns={[{ label: 'Time' }, { label: `${name} (${unit})`, numeric: true }]}
        rows={() =>
          points.map((p) => (
            <tr key={p.date.getTime()}>
              <td>{formatDateTime(p.date)}</td>
              <td className="gd-num">{formatNumber(p.value, digits)}</td>
            </tr>
          ))
        }
      />
    </div>
  );
}
